import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { getDocumentation } from '../services/DocumentationService';
import { setDocuments } from '../store/slices/documentationSlice';

interface UseDocumentSigningPollingOptions {
  pollingInterval?: number;
  onSigningComplete?: (documentId: string, fileName: string) => void;
}

// Never poll literally forever while this tab stays open. 1200 polls x 3s
// = 1 hour before this hook gives up on its own; callers can read
// `isStalled` if they want to surface a "give up" affordance.
const MAX_POLL_COUNT = 1200;

export function useDocumentSigningPolling({
  pollingInterval = 3000,
  onSigningComplete
}: UseDocumentSigningPollingOptions = {}) {
  const dispatch = useAppDispatch();
  const { documents, documentationReqBody } = useAppSelector(
    (state) => state.documentation
  );
  const isOffline = useAppSelector((state) => state.appStatus.isOffline);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousPendingRef = useRef<Set<string>>(new Set());
  const pollCountRef = useRef<number>(0);
  const reqBodyRef = useRef(documentationReqBody);
  const loadedCountRef = useRef(documents.length);
  const onSigningCompleteRef = useRef(onSigningComplete);
  const [isStalled, setIsStalled] = useState(false);

  useEffect(() => {
    reqBodyRef.current = documentationReqBody;
  }, [documentationReqBody]);

  useEffect(() => {
    loadedCountRef.current = documents.length;
  }, [documents.length]);

  useEffect(() => {
    onSigningCompleteRef.current = onSigningComplete;
  }, [onSigningComplete]);

  const hasPendingSigningDocuments = documents.some(
    (document) => document.signingStatus === 'pending'
  );

  const pollDocuments = useCallback(async () => {
    const request = reqBodyRef.current;
    if (!request.aiSystemId || isOffline) return;

    pollCountRef.current += 1;

    if (pollCountRef.current > MAX_POLL_COUNT) {
      console.warn(
        `Document signing polling exceeded ${MAX_POLL_COUNT} attempts — stopping automatically`
      );
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsStalled(true);
      return;
    }

    try {
      const limit = Math.max(loadedCountRef.current, request.limit);
      const response = await getDocumentation({
        ...request,
        offset: 0,
        limit
      });

      dispatch(setDocuments(response));

      const callback = onSigningCompleteRef.current;
      if (!callback) {
        previousPendingRef.current = new Set(
          response.items
            .filter((document) => document.signingStatus === 'pending')
            .map((document) => document.id)
        );
        return;
      }

      const currentPending = new Set(
        response.items
          .filter((document) => document.signingStatus === 'pending')
          .map((document) => document.id)
      );

      previousPendingRef.current.forEach((documentId) => {
        if (!currentPending.has(documentId)) {
          const completedDocument = response.items.find(
            (document) => document.id === documentId
          );

          if (
            completedDocument &&
            completedDocument.signingStatus === 'completed'
          ) {
            callback(completedDocument.id, completedDocument.fileName);
          }
        }
      });

      previousPendingRef.current = currentPending;
    } catch (error) {
      console.error('Document signing polling failed:', error);
    }
  }, [dispatch, isOffline]);

  useEffect(() => {
    if (isOffline && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    if (isOffline) {
      return;
    }

    if (hasPendingSigningDocuments && !intervalRef.current) {
      pollCountRef.current = 0;
      setIsStalled(false);
      void pollDocuments();

      const interval = setInterval(() => {
        void pollDocuments();
      }, pollingInterval);

      intervalRef.current = interval;
    }

    if (!hasPendingSigningDocuments && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      previousPendingRef.current.clear();
      pollCountRef.current = 0;
    }
  }, [hasPendingSigningDocuments, isOffline, pollDocuments, pollingInterval]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    isPolling: !!intervalRef.current,
    hasPendingSigningDocuments,
    isStalled
  };
}
